import { useCallback, useEffect, useRef, useState } from 'react'

interface UseTimerInterface {
  onTimeOver: () => void
  limitSeconds: number
}

export const useTimer = (params: UseTimerInterface) => {
  const time = useRef(params.limitSeconds)
  const interval = useRef<ReturnType<typeof setInterval>>()
  const [shouldStart, setShouldStart] = useState(false)

  const reset = useCallback(() => {
    time.current = params.limitSeconds
    setShouldStart(false)
  }, [params.limitSeconds])

  const start = useCallback(() => setShouldStart(true), [])

  const timeLeft = useCallback(() => time.current, [])

  const restart = useCallback(() => {
    time.current = params.limitSeconds
    setShouldStart(true)
  }, [params.limitSeconds])

  const clearTimer = useCallback((intervalId: ReturnType<typeof setInterval>) => {
    clearInterval(intervalId)
    interval.current = undefined
    setShouldStart(false)
  }, [])

  useEffect(() => {
    if (!shouldStart) {
      if (interval.current) {
        clearInterval(interval.current)
      }

      return
    }

    interval.current = setInterval(() => {
      if (time.current > 0) {
        time.current -= 1
        return
      }

      interval.current && clearTimer(interval.current)
      params.onTimeOver()
    }, 1000)

    return () => interval.current && clearTimer(interval.current)
  }, [params.onTimeOver, shouldStart])

  useEffect(() => {
    time.current = params.limitSeconds
  }, [params.limitSeconds])

  return {
    timeLeft,
    start,
    reset,
    restart,
  }
}
